import { useCallback, useEffect, useRef, useState } from "react";
import { useDataProvider, useNotify } from "react-admin";
import { Views, type View } from "react-big-calendar";
import dayjs, { type Dayjs } from "dayjs";
import { formatIsoDate } from "@/utils/date-format";
import type { HallBookingCalendarEvent } from "./types";
import { mapHallBookingRowsToCalendarEvents } from "./map-hall-bookings-calendar-events";

export function useHallBookingsCalendarEvents(view: View, date: Dayjs) {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const notifyRef = useRef(notify);
  notifyRef.current = notify;
  const fetchGenerationRef = useRef(0);
  const [events, setEvents] = useState<HallBookingCalendarEvent[]>([]);
  const dateKey = formatIsoDate(dayjs(date));

  const fetchHallBookings = useCallback(
    async (start: Date, end: Date) => {
      const { data } = await dataProvider.getList("hallBookings", {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "date", order: "ASC" },
        filter: {
          start: formatIsoDate(dayjs(start)),
          end: formatIsoDate(dayjs(end)),
          includeBlockedDates: true,
        },
      });
      return data as unknown[];
    },
    [dataProvider]
  );

  const loadEvents = useCallback(async () => {
    const generation = ++fetchGenerationRef.current;
    const selectedDate = dayjs(dateKey);
    let rangeStart: Dayjs;
    let rangeEnd: Dayjs;

    if (view === Views.MONTH) {
      rangeStart = selectedDate.startOf("month");
      rangeEnd = selectedDate.endOf("month");
    } else if (view === Views.WEEK) {
      rangeStart = selectedDate.startOf("week");
      rangeEnd = selectedDate.endOf("week");
    } else {
      rangeStart = selectedDate.startOf("day");
      rangeEnd = selectedDate.endOf("day");
    }

    try {
      const bookings = await fetchHallBookings(rangeStart.toDate(), rangeEnd.toDate());
      if (generation !== fetchGenerationRef.current) return;
      setEvents(mapHallBookingRowsToCalendarEvents(bookings));
    } catch {
      if (generation !== fetchGenerationRef.current) return;
      notifyRef.current("Error fetching hall bookings", { type: "warning" });
    }
  }, [view, dateKey, fetchHallBookings]);

  useEffect(() => {
    void loadEvents();
  }, [loadEvents]);

  return events;
}
